import React from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { Collapse, CollapsePanel } from "@mapmycustomers/ui";
import { AvatarWithName } from "@mapmycustomers/ui";

import UserPreviewCard from "@app/component/UserPreviewCard";
import { getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

import styles from "./Details.module.scss";

const messages = defineMessages({
  createdAt: {
    id: "dashboard.editModal.preview.details.createdAt",
    defaultMessage: "Date Created",
    description:
      "Date Created field title in the Details section title in Edit Card modal on the Dashboard page",
  },
  createdBy: {
    id: "dashboard.editModal.preview.details.createdBy",
    defaultMessage: "Created By",
    description:
      "Created By field title in the Details section title in Edit Card modal on the Dashboard page",
  },
  title: {
    id: "dashboard.editModal.preview.details",
    defaultMessage: "Details",
    description: "Details section title in Edit Card modal on the Dashboard page",
  },
  updatedAt: {
    id: "dashboard.editModal.preview.details.updatedAt",
    defaultMessage: "Last Modified",
    description:
      "Date Modified field title in the Details section title in Edit Card modal on the Dashboard page",
  },
  updatedBy: {
    id: "dashboard.editModal.preview.details.updatedBy",
    defaultMessage: "Modified By",
    description:
      "Updated By field title in the Details section title in Edit Card modal on the Dashboard page",
  },
});

interface DetailsProps {
  details?: Pick<DashboardCard, "createdAt" | "createdBy" | "updatedAt" | "updatedBy">;
}

interface Props extends DetailsProps {
  allUsers: User[];
}

const Details: React.FC<Props> = ({ allUsers, details }) => {
  const intl = useIntl();

  if (!details?.createdAt && !details?.createdBy && !details?.updatedAt && !details?.updatedBy) {
    return null;
  }

  const createdAtDate = details.createdAt ? parseApiDate(details.createdAt) : undefined;
  const createdByUser = details.createdBy
    ? allUsers.find(({ id }) => id === details.createdBy?.id)
    : undefined;
  const updatedAtDate = details.updatedAt ? parseApiDate(details.updatedAt) : undefined;
  const updatedByUser = details.updatedBy
    ? allUsers.find(({ id }) => id === details.updatedBy?.id)
    : undefined;

  return (
    <Collapse className={styles.container} expandIconPosition="end" ghost>
      <CollapsePanel header={intl.formatMessage(messages.title)} key="details">
        <div className={styles.content}>
          {createdByUser && (
            <>
              <div>{intl.formatMessage(messages.createdBy)}</div>
              <div>
                <UserPreviewCard userId={createdByUser.id}>
                  <div className={styles.avatar}>
                    <AvatarWithName actionOnHover user={createdByUser} />
                  </div>
                </UserPreviewCard>
              </div>
            </>
          )}
          {isValidDate(createdAtDate) && (
            <>
              <div>{intl.formatMessage(messages.createdAt)}</div>
              <div>{formatDate(createdAtDate, "PPp")}</div>
            </>
          )}
          {updatedByUser && (
            <>
              <div>{intl.formatMessage(messages.updatedBy)}</div>
              <UserPreviewCard userId={updatedByUser.id}>
                <div className={styles.avatar}>
                  <AvatarWithName actionOnHover user={updatedByUser} />
                </div>
              </UserPreviewCard>
            </>
          )}
          {isValidDate(updatedAtDate) && (
            <>
              <div>{intl.formatMessage(messages.updatedAt)}</div>
              <div>{formatDate(updatedAtDate, "PPp")}</div>
            </>
          )}
        </div>
      </CollapsePanel>
    </Collapse>
  );
};

const mapStateToProps = (state: RootState) => ({
  allUsers: getUsersOfEntireOrganization(state),
});

export default connect(mapStateToProps)(Details);
